import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  InstagramLogo,
  Storefront,
  AddressBook,
  WhatsappLogo,
  Sparkle,
  ArrowRight,
  ArrowSquareOut,
  House,
  Link as LinkIcon,
  X as XIcon,
  Browsers,
  ChatCircleDots,
  UserCircle,
} from "@phosphor-icons/react";
import { CHROME_STORE_URL } from "@/lib/extensionConstants";
import {
  SocialDrawerPreview,
  StimaDrawerPreview,
  SitoPersonaleDrawerPreview,
} from "./drawer/BentoDrawerPreviews";

/**
 * BentoGridV2 — "Dentro l'app c'è anche…": elenco chiaro di tutti i
 * servizi dentro Pronto, oltre al sopralluogo (che e' gia' mostrato
 * nell'hero animato).
 *
 * iter v33 r69 · User: «chiaramente poi devi mettere pero' tutti i
 * servizi che ci sono dentro nell'app ed essere chiari». Rifatto:
 *   • 8 tile uniformi (tutti col-span-1) invece del mix di 1/2 col
 *   • Aggiunte 2 tile mancanti: Vetrina Agente + Assistente Immobiliare
 *   • Copy sempre in "tu" verso l'agente (era mix Lei/tu confuso)
 *   • body_short piu' breve (max 55 char) per scrolling veloce mobile
 */
const TILES = [
  {
    id: "foto",
    icon: Sparkle,
    title: "Foto migliorate",
    body: "Ogni foto scattata col telefono viene rifinita in un tocco: luce, colori, mosso, oggetti di troppo tolti. Puoi rivedere prima/dopo e rifare al volo se non ti piace.",
    body_short: "Le foto migliorate in un tocco, senza Photoshop.",
    accent: "#B57C5E",
    bg: "linear-gradient(135deg, #FBF1EA 0%, #FFFFFF 100%)",
  },
  {
    id: "social",
    icon: InstagramLogo,
    title: "Post & reel social",
    body: "Post 1:1, storie e reel verticali con testo, logo e prezzo già in posizione. La musica la scegli su Instagram o TikTok al momento della pubblicazione.",
    body_short: "Post, storie e reel verticali già pronti.",
    accent: "#E1306C",
    bg: "linear-gradient(135deg, #FCEEF3 0%, #FFFFFF 100%)",
    preview: "social",
  },
  {
    id: "sito-personale",
    icon: UserCircle,
    title: "Sito personale",
    body: "Un sito al tuo nome con foto, bio, agenzia, contatti e tutti i tuoi annunci sempre aggiornati. Da mettere in bio Instagram, firma email o biglietti da visita.",
    body_short: "Il tuo sito al tuo nome con tutti gli annunci.",
    accent: "#7C8B6E",
    bg: "linear-gradient(135deg, #F1F4ED 0%, #FFFFFF 100%)",
    preview: "sito-personale",
  },
  {
    id: "vetrina-immobile",
    icon: Storefront,
    title: "Vetrina immobile",
    body: "Un mini-sito col tuo brand per ogni casa. Link condivisibile, foto, planimetria, mappa, contatti. Il cliente lo apre dal telefono, non deve scaricare niente.",
    body_short: "Un mini-sito per ogni annuncio, con link condivisibile.",
    accent: "#5A6A4D",
    bg: "#FFFFFF",
  },
  {
    id: "stima",
    icon: House,
    title: "Stima online",
    body: "Condividi il tuo link o il QR del volantino: il proprietario risponde a poche domande e riceve una prima stima. Se vuole approfondire, può lasciarti il contatto e la richiesta arriva direttamente in ProntoImmo.",
    body_short: "Link o QR per la stima: chi vuole approfondire può lasciarti direttamente il contatto.",
    accent: "#1C1C1A",
    bg: "#FAFAF7",
    preview: "stima",
  },
  {
    id: "agenda",
    icon: Calendar,
    title: "Agenda vocale",
    body: "Detti a voce: «Domani alle 11 visita con Luca Ferrari in via Garibaldi». Pronto trascrive, crea l'evento, salva il contatto e ti manda un promemoria 30 minuti prima. Sync con Google Calendar e iCloud.",
    body_short: "Detti l'appuntamento, Pronto fa tutto il resto.",
    accent: "#4A4B45",
    bg: "#FFFFFF",
  },
  {
    id: "whatsapp",
    icon: WhatsappLogo,
    title: "WhatsApp",
    body: "Campagne ai contatti, auguri di compleanno e festività, follow-up dopo le visite. Pronto ti prepara il testo già scritto: apri WhatsApp e mandi.",
    body_short: "Auguri, follow-up, campagne. Testi già pronti.",
    accent: "#25D366",
    bg: "linear-gradient(135deg, #F4FBF6 0%, #FFFFFF 100%)",
  },
  {
    id: "crm",
    icon: AddressBook,
    title: "Contatti & CRM",
    body: "Clienti, budget, zone e preferenze in un unico posto. Quando entra un immobile, Pronto ti aiuta a capire a chi proporlo per primo.",
    body_short: "Clienti, budget e preferenze. Pronto ti aiuta a trovare i match migliori.",
    accent: "#3F3D38",
    bg: "#FAFAF7",
  },
  {
    id: "assistente",
    icon: ChatCircleDots,
    title: "Assistente immobiliare",
    body: "Dubbi operativi, bozze di email e messaggi, spunti per gestire clienti e trattative. Un assistente sempre a portata di mano nel lavoro quotidiano.",
    body_short: "Dubbi operativi, bozze di messaggi e spunti per le trattative.",
    accent: "#6B6A65",
    bg: "#FAFAF7",
  },
  {
    id: "extension",
    icon: Browsers,
    title: "Pubblica sui portali",
    body: "Pronto precompila i principali campi su Immobiliare.it e Idealista. Tu controlli, completi ciò che serve e pubblichi.",
    body_short: "Estensione Chrome: principali campi precompilati su Immobiliare.it e Idealista.",
    accent: "#4285F4",
    bg: "linear-gradient(135deg, #EEF3FE 0%, #FFFFFF 100%)",
    external: true,
  },
];

export default function BentoGridV2() {
  const navigate = useNavigate();
  const [openTile, setOpenTile] = useState(null);
  const open = TILES.find((t) => t.id === openTile);

  return (
    <section
      className="py-16 sm:py-24 bg-white"
      data-testid="feature-bento-grid"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="max-w-2xl mb-10 sm:mb-14">
          <span className="uppercase text-[11px] tracking-[0.22em] font-bold text-[#7C8B6E]">
            Tutto quello che c&apos;&egrave; dentro l&apos;app
          </span>
          <h2 className="font-editorial text-[30px] sm:text-4xl lg:text-5xl mt-3 leading-[1.05] tracking-tight text-[#1C1C1A]">
            Sopralluogo, foto, social,{" "}
            <span className="italic text-[#7C8B6E]">sito, agenda e altro.</span>
          </h2>
          <p className="mt-4 text-[15px] sm:text-[16px] text-[#6B6A65] leading-relaxed">
            Tocca una funzione per il dettaglio. Quello che oggi fai con cinque programmi diversi,
            qui lo fai in uno.
          </p>
        </div>

        <div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4"
          data-testid="bento-tiles"
        >
          {TILES.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setOpenTile(t.id)}
                className="text-left rounded-2xl p-4 sm:p-5 border border-[#1C1C1A]/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-22px_rgba(28,28,26,0.18)] group"
                style={{ background: t.bg }}
                data-testid={`bento-${t.id}`}
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110"
                  style={{ background: `${t.accent}18` }}
                >
                  <Icon size={20} weight="regular" style={{ color: t.accent }} />
                </div>
                <h3 className="font-editorial text-[15.5px] sm:text-[17px] text-[#1C1C1A] tracking-tight leading-tight">
                  {t.title}
                </h3>
                <p className="mt-1.5 text-[12px] sm:text-[12.5px] text-[#6B6A65] leading-snug">
                  {t.body_short}
                </p>
              </button>
            );
          })}
        </div>

        <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4 text-[14px] text-[#3F3D38]">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-[#7C8B6E]" />
            <span>Tutto incluso. Nessun costo nascosto.</span>
          </div>
          <button
            onClick={() => navigate("/register")}
            className="ml-auto inline-flex items-center gap-2 text-[#7C8B6E] hover:text-[#1C1C1A] font-semibold underline underline-offset-4"
            data-testid="bento-cta"
          >
            Inizia gratis <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* Drawer dettaglio tile — appare dal basso */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
          onClick={() => setOpenTile(null)}
          data-testid="bento-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bento-drawer-title"
        >
          <div
            className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-6 sm:p-8 max-h-[88vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${open.accent}18` }}
              >
                <open.icon size={24} weight="regular" style={{ color: open.accent }} />
              </div>
              <button
                type="button"
                onClick={() => setOpenTile(null)}
                className="text-[#6B6A65] hover:text-[#1C1C1A] -mt-1 -mr-1 p-1"
                aria-label="Chiudi"
                data-testid="bento-drawer-close"
              >
                <XIcon size={22} weight="bold" />
              </button>
            </div>
            <h3 id="bento-drawer-title" className="font-editorial text-[24px] sm:text-[28px] text-[#1C1C1A] leading-tight tracking-tight">
              {open.title}
            </h3>
            {open.preview === "social" && <SocialDrawerPreview />}
            {open.preview === "stima" && <StimaDrawerPreview />}
            {open.preview === "sito-personale" && <SitoPersonaleDrawerPreview />}
            <p className="mt-3 text-[14.5px] text-[#3F3D38] leading-relaxed">
              {open.body}
            </p>
            {open.id === "stima" && (
              <div className="mt-4 p-3.5 bg-[#F1F4ED] rounded-lg border border-[#E5E7E0] flex items-start gap-2.5">
                <LinkIcon size={16} weight="fill" className="text-[#7C8B6E] mt-0.5 shrink-0" />
                <p className="text-[12.5px] text-[#1C1C1A] leading-relaxed">
                  Il link funziona <strong>senza app</strong>. Il cliente lo
                  apre sul telefono e vede la stima. Niente account, niente
                  download.
                </p>
              </div>
            )}
            {open.id === "extension" && (
              <div className="mt-4 p-3.5 bg-[#EEF3FE] rounded-lg border border-[#D5E2FB] flex items-start gap-2.5">
                <ArrowSquareOut size={16} weight="fill" className="text-[#4285F4] mt-0.5 shrink-0" />
                <p className="text-[12.5px] text-[#1C1C1A] leading-relaxed">
                  Installazione in 30 secondi dal <strong>Chrome Web Store</strong>.
                  Funziona sul tuo browser, niente da installare sul portale.
                </p>
              </div>
            )}
            {open.id === "extension" ? (
              <>
                <a
                  href={CHROME_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#4285F4] hover:bg-[#1A73E8] text-white px-6 py-3.5 rounded-xl font-semibold text-[14.5px] transition-colors active:scale-[0.98]"
                  data-testid="bento-drawer-cta-extension"
                  onClick={() => setOpenTile(null)}
                >
                  Aggiungi a Chrome <ArrowSquareOut size={16} weight="bold" />
                </a>
                <button
                  type="button"
                  onClick={() => { setOpenTile(null); navigate("/register"); }}
                  className="mt-2 w-full text-center text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] underline underline-offset-4"
                  data-testid="bento-drawer-cta-extension-secondary"
                >
                  Prima crea l&apos;account gratis
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => { setOpenTile(null); navigate("/register"); }}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#1C1C1A] hover:bg-black text-white px-6 py-3.5 rounded-xl font-semibold text-[14.5px] transition-colors active:scale-[0.98]"
                data-testid="bento-drawer-cta"
              >
                Inizia gratis <ArrowRight size={16} weight="bold" />
              </button>
            )}
            <p className="mt-3 text-[11.5px] text-[#6B6A65] text-center">
              Senza carta di credito · 60 crediti gratis
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
